/*
 * Inbox v2's API contract (`docs/2026-10-16-inbox-v2-build-plan.md`, cards 2, 4 and 5): the shapes
 * the server sends and the browser parses, and nothing else. Pure, so the browser bundle can import it
 * (`apps/rep-workspace/web/inbox-v2/`). None of these shapes carries a draft, a plan step, a question,
 * a material, a shadow draft or a setup preview; each is strict, so a field added by mistake fails.
 */
import { z } from "zod";
import { INBOX_LABELS } from "../inbox-label.ts";
import { CLOSURE_SKIPS, REOPEN_KEPT } from "./done-closure.ts";
import { ForwardHistorySchema, ForwardRecipientsSchema } from "./forward-contract.ts";

export const INBOX_PAGE_SIZE = 50;

/** Campaign history still importing; no conversation or lead data. */
export const InboxBackfillSchema = z.strictObject({
  backfill: z.array(
    z.strictObject({
      campaignId: z.string(),
      campaignName: z.string(),
      total: z.number().int().nonnegative(),
      done: z.number().int().nonnegative(),
      draftsPerHour: z.number().int().nonnegative().nullable(),
      maxDraftAgeDays: z.number().int().nonnegative(),
    }),
  ),
});

export const InboxLabelCodeSchema = z.enum(INBOX_LABELS);
export const InboxSectionSchema = z.enum(["do_first", "newest", "no_reply_needed"]);
/** A label view: one label, or the replies labelled with none. */
const InboxLabelViewSchema = z.union([InboxLabelCodeSchema, z.literal("none")]);
export type InboxLabelView = z.infer<typeof InboxLabelViewSchema>;
/** The rail's destinations: the inbox, what the seller answered last, what was put away or deleted. */
const InboxBoxSchema = z.enum(["inbox", "sent", "done", "snoozed", "trash"]);
export const ReplyFormatSchema = z.enum(["text", "html"]);

const InboxLabelSchema = z.strictObject({
  /** Null: read, and no label fits. */
  code: InboxLabelCodeSchema.nullable(),
  by: z.enum(["ai", "rep"]),
  /** Jev was not sure: shown, worth a rep's look. Never true for a rep's own label. */
  uncertain: z.boolean(),
});

/** A person a thread can be assigned to: one who reaches the workspace (`GET …/inbox/assignees`). */
const InboxAssigneeSchema = z.strictObject({ userId: z.string(), name: z.string() });
export type InboxAssignee = z.infer<typeof InboxAssigneeSchema>;

export const InboxConversationSchema = z.strictObject({
  /** The conversation; one plan is one conversation. */
  id: z.string(),
  company: z.string().nullable(),
  leadName: z.string().nullable(),
  /** The address the lead last wrote from, or null when the lead has not written. */
  leadAddress: z.string().nullable(),
  subject: z.string(),
  /** The start of the newest message, whitespace collapsed. */
  snippet: z.string(),
  lastMessageAt: z.string(),
  lastFrom: z.enum(["lead", "seller"]),
  /** Oldest unanswered human lead reply; null when no reply is needed. Only with expand=list. */
  waitingSince: z.iso.datetime({ offset: true }).nullable().optional(),
  /** The newest lead message, which a relabel names; null when the lead has not written. */
  leadMessageId: z.string().nullable(),
  campaign: z.strictObject({ id: z.string(), name: z.string() }),
  hasFiles: z.boolean(),
  unread: z.boolean(),
  /** When it was put away; null in the inbox. A new lead message brings it back. */
  doneAt: z.string().nullable(),
  /** When it was deleted into Trash, or null; only with `trash=1` (LEA-457). */
  trashedAt: z.string().nullable().optional(),
  /** Null while the newest lead message has not been labelled (or labels are off). */
  label: InboxLabelSchema.nullable(),
  /** Classifier data-source request, only when explicitly requested by the reader. */
  dataRequest: z.boolean().optional(),
  /**
   * Interested, and the newest lead message is flagged as asking for a meeting (the latest rep mark,
   * else Jev's answer at the floor); only with `meet=1` (LEA-494).
   */
  meetingRequest: z.boolean().optional(),
  section: InboxSectionSchema,
  snooze: z
    .strictObject({ until: z.string(), at: z.string(), by: z.string() })
    .nullable()
    .optional(),
  /**
   * Who the thread is assigned to; null for nobody. Only with `with=assignee` where assignment is
   * on (docs/2026-10-20-assignment-plan.md, card A4), so a tab that never asks never meets it.
   */
  assignee: InboxAssigneeSchema.nullable().optional(),
});
export type InboxConversation = z.infer<typeof InboxConversationSchema>;

export const InboxSearchSchema = z.strictObject({
  text: z.string(),
  operators: z.array(
    z.strictObject({
      kind: z.enum([
        "from",
        "from-address",
        "company",
        "campaign",
        "campaign-id",
        "mailbox",
        "mailbox-address",
        "has",
      ]),
      value: z.string(),
    }),
  ),
});

export const InboxPageSchema = z.strictObject({
  total: z.number().int().nonnegative(),
  offset: z.number().int().nonnegative(),
  limit: z.number().int().min(1).max(INBOX_PAGE_SIZE),
  conversations: z.array(InboxConversationSchema),
  search: InboxSearchSchema.optional(),
});
export type InboxPage = z.infer<typeof InboxPageSchema>;

const count = z.number().int().nonnegative();
export const InboxCountsSchema = z.strictObject({
  /** Conversations in the inbox: not done. */
  all: count,
  unread: count,
  /** In the inbox, labels to check: Jev unsure, no rep has settled it. */
  check: count,
  sections: z.strictObject({ do_first: count, newest: count, no_reply_needed: count }),
  /**
   * Per label view, conversations that need a reply (as `needs_reply` counts them); unlabelled
   * replies are in none of them.
   */
  labels: z.record(InboxLabelViewSchema, count),
  needs_reply: count.optional(),
  waiting: count.optional(),
  snoozed: count.optional(),
  /** In the inbox, assigned to this viewer, and assigned to nobody: only with `with=assignee`. */
  mine: count.optional(),
  unassigned: count.optional(),
  total: count.optional(),
  sent: count,
  done: count,
  /** Conversations in Trash; only with `trash=1`, which an older tab never sends. */
  trash: count.optional(),
  /** Follow-up due (LEA-486); only with `follow=1`, which an older tab never sends. */
  follow_up: count.optional(),
  /** Meeting requests (LEA-494); only with `meet=1`, which an older tab never sends. */
  meeting: count.optional(),
  /** The campaigns that have conversations here, for the campaign filter and row lines. */
  campaigns: z.array(z.strictObject({ id: z.string(), name: z.string(), count })),
});
export type InboxCounts = z.infer<typeof InboxCountsSchema>;

/** The orders the list's menu offers, and the only ones a saved view keeps. */
const LIST_SORTS = ["newest", "oldest"] as const;

/** What a list asks for. Every field is optional on the wire; the server fills the defaults. */
export const InboxListQuerySchema = z.object({
  /** Explicit response negotiation; absent keeps the previous strict browser contract. */
  expand: z.literal("list").optional(),
  dataRequest: z.literal("1").optional(),
  /** Asks for each row's `trashedAt` and the counts' `trash` (LEA-457). */
  trash: z.literal("1").optional(),
  /** Asks for the counts' `follow_up` (LEA-486). */
  follow: z.literal("1").optional(),
  /**
   * Asks for each row's `meetingRequest`, the counts' `meeting` and a thread's `meetingChanges`, and
   * takes Meeting requests out of Needs reply (LEA-494). Absent, every answer is what it was.
   */
  meet: z.literal("1").optional(),
  /** Only honored with expand=list; otherwise pages retain their previous size. */
  limit: z.coerce.number().int().min(1).max(INBOX_PAGE_SIZE).optional(),
  offset: z.coerce.number().int().nonnegative().max(1_000_000).default(0),
  box: InboxBoxSchema.default("inbox"),
  /** `next` ranks what to answer first (`read-model.ts`, `nextTier`); the list's menu offers the other two. */
  sort: z.enum([...LIST_SORTS, "next"]).default("newest"),
  status: z.enum(["needs_reply", "waiting", "follow_up", "meeting"]).optional(),
  section: InboxSectionSchema.optional(),
  label: InboxLabelViewSchema.optional(),
  campaign: z.string().min(1).max(200).optional(),
  q: z.string().trim().min(1).max(200).optional(),
  unread: z.enum(["1"]).optional(),
  /** Only conversations whose label Jev was unsure of and no rep has settled. */
  check: z.enum(["1"]).optional(),
  files: z.enum(["1"]).optional(),
  days: z.coerce.number().int().min(1).max(365).optional(),
  /**
   * Assigned to this viewer (`me`), to nobody (`none`) or to one person (their user id). Honoured
   * only where assignment is on; a server without it drops the field, so the list widens, never leaks.
   */
  assigned: z.string().min(1).max(200).optional(),
  /** Asks for each row's `assignee`, the counts' `mine` and `unassigned` and a thread's `assignment`. */
  with: z.literal("assignee").optional(),
});
export type InboxListQuery = z.infer<typeof InboxListQuerySchema>;

const InboxAttachmentSchema = z.strictObject({
  id: z.string(),
  name: z.string(),
  bytes: z.number().nonnegative().nullable(),
});

const InboxMessageSchema = z.strictObject({
  id: z.string(),
  direction: z.enum(["inbound", "outbound"]),
  from: z.string(),
  to: z.array(z.string()),
  cc: z.array(z.string()),
  at: z.string(),
  subject: z.string(),
  /** Sanitized at import (`mail-content.ts`); shown in a sandboxed frame, never as page markup. */
  html: z.string(),
  plainText: z.string(),
  attachments: z.array(InboxAttachmentSchema),
  /** This message's stored intent; null for ours or when it has not been labelled. */
  intent: InboxLabelSchema.nullable(),
});
export type InboxMessage = z.infer<typeof InboxMessageSchema>;

/** One stored decision, including confirmations, in the header's read-only history. */
const InboxLabelChangeSchema = z.strictObject({
  /** Stable audit-row key. Optional only for thread responses from earlier servers. */
  id: z.string().optional(),
  messageId: z.string(),
  from: InboxLabelCodeSchema.nullable(),
  to: InboxLabelCodeSchema.nullable(),
  by: z.enum(["ai", "rep"]),
  /** The rep's name for a relabel; null for the AI. */
  who: z.string().nullable(),
  /** Stable rep identity, including when their account no longer exists; null for AI. */
  actorId: z.string().nullable(),
  confirmed: z.boolean(),
  at: z.string(),
});
export type InboxLabelChange = z.infer<typeof InboxLabelChangeSchema>;

/** One rep mark of the meeting flag (LEA-494), beside the relabels; only with `meet=1&history=1`. */
const InboxMeetingChangeSchema = z.strictObject({
  /** `meeting:<mark id>`, so it never collides with a relabel's key. */
  id: z.string(),
  messageId: z.string(),
  /** What the rep said: a meeting request (true) or not (false). */
  meeting: z.boolean(),
  /** The rep's name; their id when the account no longer exists. */
  who: z.string().nullable(),
  actorId: z.string(),
  at: z.string(),
});
export type InboxMeetingChange = z.infer<typeof InboxMeetingChangeSchema>;

export const InboxWorkspaceSettingsSchema = z.strictObject({ showSeenBy: z.boolean() });
export const InboxSeenBySchema = z.strictObject({
  userId: z.string(),
  name: z.string(),
  at: z.iso.datetime(),
});

export const InboxThreadSchema = z.strictObject({
  conversation: InboxConversationSchema,
  messages: z.array(InboxMessageSchema),
  forwards: ForwardHistorySchema.optional(),
  changes: z.array(InboxLabelChangeSchema),
  /** Rep marks of the meeting flag; only with `meet=1` and `history=1` (LEA-494). */
  meetingChanges: z.array(InboxMeetingChangeSchema).optional(),
  /** The mailbox a reply goes from: the seller's address on the thread. */
  mailbox: z.string().nullable(),
  replyFormat: ReplyFormatSchema.nullable(),
  /** Bison's own page for the thread, validated at import; null when there is none. */
  bisonUrl: z.string().nullable(),
  /** Only with `seenBy=1` and this workspace’s Seen-by setting enabled. */
  seenBy: z.array(InboxSeenBySchema).optional(),
  /**
   * Only with `bounces=1` (LEA-311): the bounces of this thread's own sent answers, each naming the
   * sent message by its id. Read-only: Bison stops a bounced lead, LeadCue only says it bounced.
   */
  bounces: z
    .array(
      z.strictObject({
        messageId: z.string(),
        at: z.iso.datetime({ offset: true }),
        kind: z.enum(["hard", "soft", "unplaced"]),
        bisonUrl: z.url().nullable(),
      }),
    )
    .optional(),
  /** Active snooze for handover only; never wakes the pane's expanded-list controls. */
  handoverSnooze: InboxConversationSchema.shape.snooze,
  /** Latest assignment row, including manual Unassign; only with `assignmentRecord=1`. */
  assignmentRecord: z
    .strictObject({
      source: z.enum(["manual", "rule"]),
      assignedBy: z.string().nullable(),
      assignedAt: z.iso.datetime({ offset: true }),
    })
    .nullable()
    .optional(),
  /**
   * Only with `with=assignee` where assignment is on: the assignee and the revision a single
   * reassignment sends back as `expectedRevision` (0 while the thread has never been assigned).
   */
  assignment: z
    .strictObject({
      assignee: InboxAssigneeSchema.nullable(),
      revision: z.number().int().min(0),
    })
    .optional(),
});
export type InboxThread = z.infer<typeof InboxThreadSchema>;

/** Unversioned responses keep the strict shape understood by already-open browser tabs. */
export function previousInboxThread(thread: InboxThread) {
  const { meetingChanges: _meetingChanges, ...rest } = thread;
  return {
    ...rest,
    messages: thread.messages.map(({ intent: _intent, ...message }) => message),
    changes: thread.changes
      .filter((change) => !change.confirmed)
      .map(({ id: _id, actorId: _actorId, confirmed: _confirmed, ...change }) => change),
  };
}

const InboxBulkRequestSchema = z.strictObject({
  planIds: z
    .array(z.string().min(1).max(200))
    .min(1)
    .max(INBOX_PAGE_SIZE * 25),
});
export const InboxSnoozeRequestSchema = InboxBulkRequestSchema.extend({
  until: z.iso.datetime({ offset: true }),
});
export const InboxUnsnoozeRequestSchema = InboxBulkRequestSchema;
export const InboxSnoozeResultSchema = z.strictObject({
  previous: z.array(z.strictObject({ planId: z.string(), until: z.string().nullable() })),
});

/**
 * Private views deliberately cannot store pagination, the legacy presentation section, or the `next`
 * order: that is the reading pane's next reply, not on the list's menu, and a stored value an older
 * build cannot read would make a rollback refuse the person's views.
 */
const InboxViewQuerySchema = InboxListQuerySchema.omit({
  offset: true,
  limit: true,
  section: true,
  // Not stored yet: a view keeping `assigned` sets a rollback floor (assignment plan, section 5).
  assigned: true,
  with: true,
  trash: true,
  follow: true,
  // Not stored: Meeting requests is not a view's status, and `meet` only shapes the answer (LEA-494).
  meet: true,
})
  // Trash is not a view's box (LEA-457): a stored "trash" would make a rollback refuse the views.
  .extend({
    sort: z.enum(LIST_SORTS).default("newest"),
    box: InboxBoxSchema.exclude(["trash"]).default("inbox"),
    // Follow-up due and Meeting requests are not a view's status (LEA-486, LEA-494), for the same reason.
    status: z.enum(["needs_reply", "waiting"]).optional(),
  })
  .strict();
export type InboxViewQuery = z.infer<typeof InboxViewQuerySchema>;
export const InboxViewCreateSchema = z.strictObject({
  name: z.string().trim().min(1).max(80),
  query: InboxViewQuerySchema,
  position: z.number().int().min(0).max(19).default(0),
});
export const InboxViewUpdateSchema = InboxViewCreateSchema.partial()
  .extend({ position: z.number().int().min(0).max(19).optional() })
  .refine((value) => Object.keys(value).length > 0);
export const InboxViewSchema = InboxViewCreateSchema.extend({ id: z.uuid() });
export const InboxViewsSchema = z.strictObject({ views: z.array(InboxViewSchema).max(20) });
export const InboxSuggestQuerySchema = z.strictObject({ q: z.string().trim().min(1).max(200) });
export const InboxSuggestionsSchema = z.strictObject({
  suggestions: z
    .array(
      z.strictObject({
        kind: z.enum(["from", "company", "campaign", "mailbox"]),
        label: z.string(),
        value: z.string(),
        query: z.string(),
      }),
    )
    .max(12),
});

export const InboxDoneRequestSchema = InboxBulkRequestSchema.extend({ done: z.boolean() });
/** Delete into Trash (`trashed: true`) or restore from it (LEA-457). */
export const InboxTrashRequestSchema = InboxBulkRequestSchema.extend({ trashed: z.boolean() });

/** The people a thread here may be assigned to, and which of them is the viewer. */
export const InboxAssigneesSchema = z.strictObject({
  you: z.string(),
  people: z.array(InboxAssigneeSchema),
});
export type InboxAssignees = z.infer<typeof InboxAssigneesSchema>;
/**
 * Assign threads to a person, or to nobody (`userId` null). `expectedRevision` is for one thread
 * only: a reassignment made from what the reading pane showed is refused if it changed since.
 */
export const InboxAssignRequestSchema = InboxBulkRequestSchema.extend({
  userId: z.string().min(1).max(200).nullable(),
  expectedRevision: z.number().int().min(0).optional(),
}).refine(
  (request) => request.expectedRevision === undefined || request.planIds.length === 1,
  "expectedRevision names one thread",
);
export type InboxAssignRequest = z.infer<typeof InboxAssignRequestSchema>;
const InboxAssignmentStateSchema = z.strictObject({
  planId: z.string(),
  assignee: InboxAssigneeSchema.nullable(),
  revision: z.number().int().min(0),
});
/** Each thread of this workspace the request named, after the change; others are left out. */
export const InboxAssignResultSchema = z.strictObject({
  assignments: z.array(InboxAssignmentStateSchema),
});
export type InboxAssignResult = z.infer<typeof InboxAssignResultSchema>;
/** HTTP 409: the thread was reassigned since it was read; what it is now. */
export const InboxAssignmentChangedSchema = InboxAssignmentStateSchema.omit({
  planId: true,
}).extend({
  error: z.literal("assignment_changed"),
});
export const InboxSeenRequestSchema = InboxBulkRequestSchema.extend({ seen: z.boolean() });
/** What each conversation was before a bulk change, so Undo puts each back as it was. */
export const InboxBulkResultSchema = z.strictObject({
  previous: z.array(z.strictObject({ planId: z.string(), value: z.boolean() })),
});

/**
 * Done's answer (LEA-225). `previous` is every conversation's Done before, for Undo. Only a viewer
 * with the reply agent is told what Done did to the agent's work: which drafts it closed and which
 * it left (`done-closure.ts`), or, going back to the inbox, which drafts came back and which stayed
 * as they were. Everyone else's Done closes the same work and hears only that it was done.
 */
export const InboxDoneResultSchema = z.strictObject({
  previous: z.array(z.strictObject({ planId: z.string(), value: z.boolean() })),
  closures: z.array(z.strictObject({ planId: z.string(), decisionId: z.string() })).optional(),
  skipped: z
    .array(z.strictObject({ planId: z.string(), reason: z.enum(CLOSURE_SKIPS) }))
    .optional(),
  reopened: z.array(z.string()).optional(),
  kept: z.array(z.strictObject({ planId: z.string(), reason: z.enum(REOPEN_KEPT) })).optional(),
});
export type InboxDoneResult = z.infer<typeof InboxDoneResultSchema>;

export const InboxAccessSchema = z.strictObject({
  replyAgent: z.boolean(),
  inbox: z.boolean(),
  workspaceAdmin: z.boolean(),
});
export type InboxAccess = z.infer<typeof InboxAccessSchema>;

/** One file in a reply box: what the server holds, never its bytes. */
const ReplyFileSchema = z.strictObject({
  id: z.uuid(),
  fileName: z.string().min(1).max(255),
  mediaType: z.string().min(3).max(200),
  bytes: z.number().int().positive(),
  sha256: z.string().regex(/^[a-f0-9]{64}$/u),
});
export type ReplyFile = z.infer<typeof ReplyFileSchema>;

/** A reply box's files as the server holds them; the revision changes with every change. */
export const ReplyFileSelectionSchema = z.strictObject({
  composerId: z.uuid(),
  revision: z.number().int().nonnegative(),
  files: z.array(ReplyFileSchema),
});
export type ReplyFileSelection = z.infer<typeof ReplyFileSelectionSchema>;

/**
 * A reply box's files and whether more may be added here now; when not, why: no sending here, the
 * workspace's switch off, a sender that would not carry them, Bison's units not settled, or no store.
 */
export const ReplyFilesStatusSchema = z.strictObject({
  available: z.boolean(),
  reason: z
    .enum([
      "sending_off",
      "switched_off",
      "update_required",
      "limits_unsettled",
      "storage_unavailable",
    ])
    .optional(),
  limits: z.strictObject({
    fileBytes: z.number().int().positive(),
    combinedBytes: z.number().int().positive(),
  }),
  selection: ReplyFileSelectionSchema,
});
export type ReplyFilesStatus = z.infer<typeof ReplyFilesStatusSchema>;

/** The rep's own words; a simulated compose request never becomes a real send. */
export const InboxSendRequestSchema = z
  .strictObject({
    operationId: z.uuid(),
    composerRevision: z.number().int().positive().max(2_147_483_647).optional(),
    messageId: z.string().regex(/^bison:(?:reply|sent_email):[1-9]\d*$/u),
    reply: z
      .strictObject({
        seenThrough: z.string().min(1).max(200),
        /** The preset the rep started from: Reply (false) or Reply all (true). */
        all: z.boolean(),
        /**
         * The To, Cc and Bcc the rep reviewed, frozen with the operation and sent exactly. Forward's
         * rules. Absent from a client before editable reply recipients: the server fills the preset.
         */
        recipients: ForwardRecipientsSchema.optional(),
      })
      .optional(),
    /** Present only for an explicit human Forward; frozen with this operation. */
    forwardTo: z.email().max(254).optional(),
    forward: z
      .strictObject({
        recipients: ForwardRecipientsSchema,
        sourceFingerprint: z.string().regex(/^[a-f0-9]{64}$/u),
        localFingerprint: z.string().regex(/^[a-f0-9]{64}$/u),
      })
      .optional(),
    html: z.string().max(100_000),
    plainText: z.string().min(1).max(100_000),
    simulation: z.literal(false),
    format: ReplyFormatSchema,
    /**
     * The reply box's files, exactly as the rep sees them: the box, its revision and its files in
     * order. Absent for a reply with none, which is the request every earlier client sends.
     */
    files: z
      .strictObject({
        composerId: z.uuid(),
        revision: z.number().int().nonnegative(),
        fileIds: z.array(z.uuid()).min(1).max(1_000),
      })
      .optional(),
  })
  .refine(
    (request) =>
      (!request.messageId.startsWith("bison:sent_email:") || !!request.forward) &&
      (!request.reply || !request.forwardTo) &&
      (!request.forward || request.forwardTo === request.forward.recipients.to[0]),
    "Forward recipients must match the explicit forward intent",
  );
export type InboxSendRequest = z.infer<typeof InboxSendRequestSchema>;
/**
 * An unknown send is resolved by looking in Bison, never by sending again
 * (docs/2026-10-20-send-reconciliation-plan.md): either still being looked for, or stopped and
 * waiting for a person.
 */
const SendCheckSchema = z.strictObject({
  /** Looks made so far. */
  checks: z.number().int().nonnegative(),
  /** When the next look is due; null once looking has stopped. */
  nextAt: z.iso.datetime({ offset: true }).nullable(),
  /**
   * Why looking stopped: nothing found within seven days (`unconfirmed`), more than one Sent copy
   * could be this send (`ambiguous`), or its words could not be rebuilt (`request_unresolved`).
   */
  stopped: z.enum(["unconfirmed", "ambiguous", "request_unresolved"]).nullable(),
  /** With `ambiguous`: when Bison dated each copy that could be it. */
  candidates: z.array(z.iso.datetime({ offset: true })),
});
export type SendCheck = z.infer<typeof SendCheckSchema>;

/**
 * A person's answer once looking has stopped (`docs/2026-10-22-forward-release-decisions.md`,
 * Change A): `went_out` settles the send as sent, `not_sent` frees the conversation for a new
 * send. Neither sends anything. `candidateAt` names which of an ambiguous send's Sent copies it is.
 */
export const SendSettlementRequestSchema = z.strictObject({
  id: z.string().min(1),
  action: z.enum(["went_out", "not_sent"]),
  candidateAt: z.iso.datetime({ offset: true }).optional(),
});
export type SendSettlementRequest = z.infer<typeof SendSettlementRequestSchema>;
export const InboxSendStatusSchema = z.strictObject({
  enabled: z.boolean(),
  forwardEnabled: z.boolean().optional(),
  send: z
    .strictObject({
      id: z.string(),
      forwardTo: z.email().optional(),
      sourceMessageId: z.string().optional(),
      recipients: ForwardRecipientsSchema.optional(),
      deliveredSubject: z.string().nullable().optional(),
      state: z.enum(["queued", "claimed", "sent", "cancelled", "uncertain", "refused"]),
      holdUntil: z.string(),
      /**
       * Why a refused send never left, a stable code; or, with `queued`, `update_required` for a send
       * held until a build that can make it runs.
       */
      reason: z.string().nullable().optional(),
      /** Only with `uncertain`: how the look in Bison for this send's Sent copy stands. */
      check: SendCheckSchema.optional(),
      /** The files frozen with this send, in order; absent for a send with none. */
      files: z
        .array(z.strictObject({ fileName: z.string(), bytes: z.number().int().positive() }))
        .optional(),
      /**
       * With `sent` and files, the rep's or a forward's own (the forwarded email's files, which Bison
       * attaches by itself): whether Bison listed every one on the sent reply. False means the reply
       * went and its files could not be confirmed, which is a reason to look in Bison, never to resend.
       */
      filesConfirmed: z.boolean().optional(),
    })
    .nullable(),
});
export type InboxSendStatus = z.infer<typeof InboxSendStatusSchema>;

/**
 * Legacy unseen response, retained unchanged for already-open tabs.
 */
export const InboxUnseenSchema = z.strictObject({ unseen: z.number().int().nonnegative() });

/** The switcher counts work remaining for this viewer, independently of personal seen marks. */
export const InboxNeedsReplySchema = z.strictObject({ needsReply: z.number().int().nonnegative() });

/**
 * Whether search covers this workspace's whole list (`GET …/inbox/search-status`): `complete` when
 * every listed conversation's newest snapshot is indexed and none failed, `partial` otherwise, and
 * `unknown` before the list's projection has been filled, when completeness cannot be shown. Only a
 * complete search may say a query matched nothing.
 */
export const InboxSearchStatusSchema = z.strictObject({
  state: z.enum(["complete", "partial", "unknown"]),
  /** Listed conversations: those with a lead message. */
  eligible: z.number().int().nonnegative().nullable(),
  /** Of those, indexed at the snapshot the list shows. */
  searchable: z.number().int().nonnegative().nullable(),
  /** Of those, whose last indexing failed. */
  failed: z.number().int().nonnegative().nullable(),
});
export type InboxSearchStatus = z.infer<typeof InboxSearchStatusSchema>;

/**
 * This viewer's inbox revision here (`GET …/inbox/revision`): a token that changes when anything
 * the inbox shows them may have, so an open inbox reads its lists again only when it moved (W5).
 */
export const InboxRevisionSchema = z.strictObject({ revision: z.string() });
